import { useCallback, useMemo } from "react";
import { Box, Checkbox, Icon, Label, Text, type IconColor } from "@salestrak/ui";
import { ISSUE_AGE_STATUSES } from "#app/lib/data";
import { AGE_STATUS_CONFIG } from "#app/lib/age-status";

// `decoColor` — the palette hue every age clock paints (see `AgeLabel`), so
// the filter rows match the cells they filter.
const AGE_STATUS_OPTIONS: {
  value: string | null;
  label: string;
  color: IconColor;
  icon: "circle-pause" | "clock";
}[] = [
  {
    value: null,
    label: "Disabled",
    color: "muted",
    icon: "circle-pause",
  },
  ...ISSUE_AGE_STATUSES.map((key) => ({
    value: key as string | null,
    label: AGE_STATUS_CONFIG[key].label,
    color: AGE_STATUS_CONFIG[key].decoColor,
    icon: "clock" as const,
  })),
];

type AgeStatusInputProps = {
  value: unknown;
  onChange: (value: unknown) => void;
};

export function AgeStatusInput({ value, onChange }: AgeStatusInputProps) {
  const selectedValues = useMemo(
    () => (Array.isArray(value) ? (value as (string | null)[]) : []),
    [value],
  );

  const toggleValue = useCallback(
    (optionValue: string | null) => {
      const next = selectedValues.includes(optionValue)
        ? selectedValues.filter((v) => v !== optionValue)
        : [...selectedValues, optionValue];
      onChange(next.length > 0 ? next : null);
    },
    [selectedValues, onChange],
  );

  return (
    // `Label` is inline-flex; a block parent would flow the rows inline and
    // wrap them. The column groups them as one stack.
    <Box direction="column" gap="sm">
      {AGE_STATUS_OPTIONS.map((opt) => {
        const checked = selectedValues.includes(opt.value);
        return (
          // Checkbox has no label slot; the `Label` wraps both parts.
          <Label key={String(opt.value)}>
            <Checkbox
              checked={checked}
              onCheckedChange={() => toggleValue(opt.value)}
            />
            <Icon name={opt.icon} size="md" color={opt.color} />
            <Text size="sm" weight="normal">
              {opt.label}
            </Text>
          </Label>
        );
      })}
    </Box>
  );
}
